/* eslint-disable jira/filename-case */

import { metrics } from '@atlassian/browser-metrics/factory';
import type { Metric } from '@atlassian/browser-metrics/metric';
import performance from '@atlassian/jira-common-performance/src/performance.tsx';
import { UFOExperience } from '@atlassian/ufo-experimental/experience';
import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
} from '@atlassian/ufo-experimental/experience-types';
import { PERFORMANCE_KEYS } from '../../../constants.tsx';
import type {
	InteractionMetric,
	PageSegmentLoadMetric,
	PerformanceMetric,
	Timing,
} from './types.tsx';

export const forgeModulesConcurrentFetchTime: (concurrentId: string | number) => Metric =
	metrics.concurrent.custom({
		key: PERFORMANCE_KEYS.FORGE_MODULES.FETCH_TIME,
	});

export const forgeModulesConcurrentLoadTime: (concurrentId: string | number) => Metric =
	metrics.concurrent.custom({
		key: PERFORMANCE_KEYS.FORGE_MODULES.LOAD_TIME,
	});

const workItemConcurrentExperiences = new Map<string, UFOExperience>();

type StartConcurrentWorkItemExperienceOptions = {
	startTime?: number;
};

type StopConcurrentWorkItemExperienceOptions = {
	stopTime?: number;
	customData?: Parameters<UFOExperience['addMetadata']>[0];
};

export const toWorkItemUfoKey = (eventId: string): string => `work-item.${eventId}`;

const getConcurrentWorkItemExperienceId = (
	ufoExperienceKey: string,
	concurrentId: string | number,
): string => `${ufoExperienceKey}:${concurrentId}`;

const assignExperienceMetricTime = (
	experience: UFOExperience,
	metricTime: { startTime?: number; endTime?: number },
): void => {
	Object.assign(experience.metrics, metricTime);
};

export const startConcurrentWorkItemExperience = (
	concurrentId: string | number,
	ufoExperienceKey: string,
	options: StartConcurrentWorkItemExperienceOptions = {},
): void => {
	const experienceId = getConcurrentWorkItemExperienceId(ufoExperienceKey, concurrentId);
	const existingExperience = workItemConcurrentExperiences.get(experienceId);

	if (existingExperience) {
		existingExperience.abort();
	}

	const experience = new UFOExperience(ufoExperienceKey, {
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
	});

	experience.start();
	if (options.startTime !== undefined) {
		assignExperienceMetricTime(experience, { startTime: options.startTime });
	}
	workItemConcurrentExperiences.set(experienceId, experience);
};

export const stopConcurrentWorkItemExperience = (
	concurrentId: string | number,
	ufoExperienceKey: string,
	options: StopConcurrentWorkItemExperienceOptions = {},
): void => {
	const experienceId = getConcurrentWorkItemExperienceId(ufoExperienceKey, concurrentId);
	const experience = workItemConcurrentExperiences.get(experienceId);

	if (!experience) {
		return;
	}

	const { customData, stopTime } = options;

	if (customData != null) {
		experience.addMetadata(customData);
	}
	if (stopTime !== undefined) {
		assignExperienceMetricTime(experience, { endTime: stopTime });
	}
	experience.success();
	workItemConcurrentExperiences.delete(experienceId);
};

export const cancelConcurrentWorkItemExperience = (
	concurrentId: string | number,
	ufoExperienceKey: string,
): void => {
	const experienceId = getConcurrentWorkItemExperienceId(ufoExperienceKey, concurrentId);
	const experience = workItemConcurrentExperiences.get(experienceId);

	if (!experience) {
		return;
	}

	experience.abort();
	workItemConcurrentExperiences.delete(experienceId);
};

export const realTimeIssueEventInvokeTime: UFOExperience = new UFOExperience(
	PERFORMANCE_KEYS.REAL_TIME_ISSUE_EVENTS.INVOKE_TIME,
	{
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
	},
);

export async function measureInitialPageLoadAsyncResourcesTiming<T>(
	startMark: string,
	endMark: string,
	fn: () => Promise<T>,
): Promise<T> {
	if (
		__SERVER__ ||
		performance.getEntriesByName(startMark).length > 0 ||
		performance.getEntriesByName(endMark).length > 0
	) {
		return fn();
	}

	performance.mark(startMark);
	const result = await fn();
	performance.mark(endMark);
	return result;
}

export const markAdditionalTimingsFromWindowPerformance = (
	metric: PerformanceMetric,
	additionalTimings: Timing[],
): void => {
	additionalTimings
		.flatMap(({ startMark, endMark, cleanStart, cleanEnd }) => [
			...performance.getEntriesByName(startMark).map((entry) => ({
				name: entry.name,
				startTime: entry.startTime,
				cleanStart,
				cleanEnd: undefined,
			})),
			...performance.getEntriesByName(endMark).map((entry) => ({
				name: entry.name,
				startTime: entry.startTime,
				cleanStart: undefined,
				cleanEnd,
			})),
		])
		.forEach(({ name, startTime, cleanStart, cleanEnd }) => {
			metric.mark(name, startTime);

			if (cleanStart || cleanEnd) {
				performance.clearMarks(name);
			}
		});
};

export const customFieldPageSegmentLoadMetric = (
	additionalTimings: Timing[] = [],
): PageSegmentLoadMetric =>
	metrics.pageSegmentLoad({
		key: 'forge-custom-field',
		timings: [
			...additionalTimings,
			{
				key: 'tti',
				startMark: PERFORMANCE_KEYS.MARKS.TTI_START,
				endMark: PERFORMANCE_KEYS.MARKS.TTI_END,
			},
		],
	});

export const customFieldInteractionMetric = (additionalTimings: Timing[] = []): InteractionMetric =>
	metrics.interaction({
		key: 'forge-custom-field',
		timings: [...additionalTimings],
	});
